import { motion, useScroll, useTransform } from "framer-motion";
import { ArrowDown } from "lucide-react";
import { useSiteSettings, useContentSection } from "@/lib/siteData";

const HERO_IMG = `${process.env.PUBLIC_URL}/hero-jetski.jpg`;

const lineAnim = (i) => ({
    initial: { y: "115%" },
    animate: { y: 0 },
    transition: {
        duration: 1,
        delay: 0.35 + i * 0.14,
        ease: [0.16, 1, 0.3, 1],
    },
});

export default function Hero({ scrollToId }) {
    const { scrollY } = useScroll();
    const bgY = useTransform(scrollY, [0, 900], [0, 180]);
    const settings = useSiteSettings();
    const hero = useContentSection("hero");

    const lines = (hero.title || "").split("\n").filter((t) => t.trim());
    const lastIdx = lines.length - 1;

    return (
        <section
            id="top"
            className="relative flex min-h-[100svh] flex-col justify-start overflow-hidden sm:min-h-screen sm:justify-end"
        >
            <motion.div
                style={{ y: bgY }}
                className="absolute inset-0 z-0"
            >
                <motion.img
                    src={HERO_IMG}
                    loading="eager"
                    fetchPriority="high"
                    decoding="async"
                    alt="Piloto de jet ski acelerando em águas cristalinas com floresta tropical ao fundo, em Ubatuba"
                    initial={{ scale: 1.18 }}
                    animate={{ scale: 1 }}
                    transition={{ duration: 2.4, ease: [0.16, 1, 0.3, 1] }}
                    className="h-[115%] w-full object-cover"
                />
                <div className="absolute inset-0 bg-gradient-to-b from-ocean/70 via-ocean/35 to-ocean" />
                <div className="absolute inset-0 bg-gradient-to-r from-ocean/80 via-ocean/20 to-transparent" />
            </motion.div>

            <div className="relative z-10 mx-auto grid w-full max-w-7xl grid-cols-1 gap-5 px-4 pb-10 pt-36 sm:gap-10 sm:px-8 sm:pb-16 sm:pt-36 lg:grid-cols-12 lg:items-end lg:pb-24">
                <div className="lg:col-span-8 lg:-translate-y-40 xl:translate-x-12 2xl:translate-x-24">
                    <motion.p
                        initial={{ opacity: 0, y: 14 }}
                        animate={{ opacity: 1, y: 0 }}
                        transition={{ duration: 0.7, delay: 0.15 }}
                        className="font-mono2 text-[11px] font-semibold uppercase tracking-[0.3em] text-neon-cyan sm:text-xs"
                        data-testid="hero-eyebrow"
                    >
                        {hero.extra.eyebrow}
                    </motion.p>

                    <h1 className="mt-4 font-display text-[10vw] font-black uppercase leading-[1.03] sm:text-5xl md:text-6xl lg:text-[5.5rem] xl:text-[6.2rem]">
                        {lines.map((text, i) => (
                            <span key={`${text}-${i}`} className="block overflow-hidden">
                                <motion.span
                                    className={`block ${
                                        i === lastIdx
                                            ? "text-transparent bg-clip-text bg-gradient-to-r from-neon-cyan via-neon-teal to-neon-cyan"
                                            : "text-ink"
                                    }`}
                                    {...lineAnim(i)}
                                >
                                    {text}
                                </motion.span>
                            </span>
                        ))}
                    </h1>

                    <motion.p
                        initial={{ opacity: 0, y: 18 }}
                        animate={{ opacity: 1, y: 0 }}
                        transition={{ duration: 0.8, delay: 0.95 }}
                        className="mt-6 max-w-xl text-base leading-relaxed text-dim sm:text-lg"
                    >
                        {hero.subtitle}
                    </motion.p>


                </div>

                <motion.div
                    initial={{ opacity: 0, y: 26 }}
                    animate={{ opacity: 1, y: 0 }}
                    transition={{ duration: 0.9, delay: 1.25 }}
                    className="hidden sm:block lg:col-span-4 lg:justify-self-end"
                >
                    <div
                        data-testid="hero-price-badge"
                        className="floaty w-full max-w-xs rounded-3xl border border-neon-cyan/25 bg-deep/70 p-7 backdrop-blur-xl"
                    >
                        <p className="font-mono2 text-[10px] font-semibold uppercase tracking-[0.3em] text-neon-cyan">
                            Passeios de jet ski
                        </p>
                        <p className="mt-3 font-display text-4xl font-black text-ink">
                            a partir de
                        </p>
                        <p className="font-display text-5xl font-black text-neon-cyan">
                            {settings.hero_price}
                        </p>
                        <p className="mt-4 text-sm leading-relaxed text-dim">
                            Consulte as durações e tarifas na tabela de jet ski.
                        </p>
                    </div>
                </motion.div>
            </div>

            <motion.div
                initial={{ opacity: 0 }}
                animate={{ opacity: 1 }}
                transition={{ delay: 1.8, duration: 1 }}
                className="pointer-events-none absolute bottom-6 left-1/2 z-10 hidden -translate-x-1/2 items-center gap-3 lg:flex"
            >
                <span className="font-mono2 text-[10px] uppercase tracking-[0.35em] text-dim">
                    role para explorar
                </span>
                <ArrowDown className="h-3.5 w-3.5 animate-bounce text-neon-cyan" />
            </motion.div>
        </section>
    );
}
